/**
 * The rules and state behind `LabelInput`: which suggestions a typed text offers, how a typed name joins the list
 * (trimmed, capped, never twice ignoring case) and the keyboard. Names compare ignoring case because the server
 * reuses a label that way, so "aws" and "AWS" are one label.
 */

import { type KeyboardEvent, useState } from 'react';

import { assetCreateInPartnersOneItemMax } from '@/api/generated/schemas';

/** The default cap: the longest label the API takes (a partner name on an asset). */
export const LABEL_MAX = assetCreateInPartnersOneItemMax;

export interface LabelOption {
  value: string;
  /** A name nothing carries yet: the listbox offers it as `Add "<name>"`. */
  isNew: boolean;
}

const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();

/** One space between words, none around, capped: "  AWS   Cloud " is "AWS Cloud", as the server stores it. */
export function cleanLabel(text: string, max = LABEL_MAX): string {
  return text.replace(/\s+/g, ' ').trim().slice(0, max);
}

/** The suggestion spelled like `text` ignoring case, else `text` itself, trimmed and capped. */
export function canonical(text: string, suggestions: string[], max = LABEL_MAX): string {
  const name = cleanLabel(text, max);
  return suggestions.find((s) => same(s, name)) ?? name;
}

/** `value` with `name` appended; the same array when the name is empty or already there. */
export function withLabel(value: string[], name: string): string[] {
  if (!name || value.some((v) => same(v, name))) return value;
  return [...value, name];
}

/** The suggestions holding the typed text and not chosen yet, then the typed text itself when it is a new name. */
export function labelOptions(suggestions: string[], value: string[], text: string, max = LABEL_MAX): LabelOption[] {
  const typed = cleanLabel(text, max);
  const needle = typed.toLowerCase();
  const options = suggestions
    .filter((s) => !value.some((v) => same(v, s)) && s.toLowerCase().includes(needle))
    .map((s) => ({ value: s, isNew: false }));
  if (typed && !suggestions.some((s) => same(s, typed)) && !value.some((v) => same(v, typed))) {
    options.push({ value: typed, isNew: true });
  }
  return options;
}

export function useLabelInput({
  value,
  onChange,
  suggestions,
  maxLength = LABEL_MAX,
}: {
  value: string[];
  onChange: (value: string[]) => void;
  suggestions: string[];
  maxLength?: number;
}) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const options = labelOptions(suggestions, value, text, maxLength);
  const shown = open && options.length > 0;

  function add(names: string[]) {
    const next = names.reduce((list, name) => withLabel(list, canonical(name, suggestions, maxLength)), value);
    if (next !== value) onChange(next);
    setActive(-1);
  }

  function commit() {
    const picked = shown ? options[active] : undefined;
    add([picked ? picked.value : text]);
    setText('');
  }

  // A comma ends a name, so typing "AWS," or pasting "AWS, Azure" adds each name before the last comma.
  function type(next: string) {
    const parts = next.split(',');
    const rest = parts.pop() ?? '';
    if (parts.length > 0) add(parts);
    setText(rest);
    setOpen(true);
    setActive(-1);
  }

  function move(step: 1 | -1) {
    if (!shown) return setOpen(true);
    setActive((i) => Math.max(-1, Math.min(options.length - 1, i + step)));
  }

  function close() {
    setOpen(false);
    setActive(-1);
  }

  // Each key's handler says whether it took the key; a key it leaves keeps its default.
  const keys: Record<string, () => boolean> = {
    Backspace: () => text === '' && value.length > 0 && (onChange(value.slice(0, -1)), true),
    // An empty Enter is left alone, so it still submits the form around the field.
    Enter: () => (text.trim() !== '' || (shown && active >= 0)) && (commit(), true),
    ArrowDown: () => (move(1), true),
    ArrowUp: () => (move(-1), true),
    // Only an open list takes Escape; otherwise it reaches a dialog around the field.
    Escape: () => shown && (close(), true),
  };

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (!keys[e.key]?.()) return;
    if (e.key !== 'Backspace') e.preventDefault();
    e.stopPropagation();
  }

  return {
    text,
    options,
    shown,
    active,
    type,
    onKeyDown,
    pick: (option: LabelOption) => {
      add([option.value]);
      setText('');
    },
    remove: (name: string) => onChange(value.filter((v) => v !== name)),
    open: () => setOpen(true),
    // Leaving the field keeps the typed name (not a highlighted one) rather than dropping it unseen before a save.
    blur: () => {
      add([text]);
      setText('');
      close();
    },
  };
}
